import type * as ngCore from '@angular/core'
import type * as rxjs from 'rxjs'

import type { Feature } from '../feature'

import { Selector } from './selector'

/**
 * Provides different methods to access a selector.
 *
 * @see {@link BoundSelector.asObservable}, {@link BoundSelector.asSignal},
 * {@link BoundSelector.asSelector}
 */
export class BoundSelector<out TSelection> {
  /** @see {@link rxjs.Observable} */
  public get asObservable() {
    return this.feature.store.select(this.asSelector)
  }
  /** @see {@link ngCore.Signal} */
  public get asSignal() {
    return this.feature.store.selectSignal(this.asSelector)
  }
  /** @see {@link Selector} */
  public get asSelector(): Selector<TSelection> {
    return (this._selector ??= this._producer())
  }

  /** @see {@link ngrxStore.Store} */
  public readonly feature: Feature.Default
  /** TODO:  */
  private _selector: Selector<TSelection> | undefined

  constructor(
    /** @see {@link ngrxStore.Store} */
    feature: Feature.Constraint,
    /**
     * Used to lazily produce selector
     *
     * @see {@link Selector}
     */
    private readonly _producer: () => Selector<TSelection>,
  ) {
    this.feature = feature
  }

  /**
   * Creates a new {@link BoundSelector} whose result is created by passing the
   * result of this {@link BoundSelector} to the given function
   */
  public project<TSelectors extends readonly Selector.Fn<any, any>[], TResult>(
    ...args: Selector.Factory.Args<
      TSelectors,
      TResult,
      [typeof this.asSelector]
    >
  ): BoundSelector<TResult> {
    return new BoundSelector(this.feature, () =>
      Selector.create<[typeof this.asSelector, ...TSelectors], TResult>(
        this.asSelector,
        ...args,
      ),
    )
  }
}
